import { createContext, useContext, useEffect, useState } from "react";
import { applyGoogleConsent } from "@/lib/googleConsent";

export const POLICY_VERSION = "2026-01-11";
const KEY = "djspy_consent_v1";
const CATS = ["analytics", "advertising"];

const ConsentCtx = createContext(null);

function readGPC() {
  try { return !!(navigator && navigator.globalPrivacyControl); } catch { return false; }
}

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p.policy_version !== POLICY_VERSION) return null;
    return p;
  } catch { return null; }
}

function save(prefs) {
  const doc = { ...prefs, policy_version: POLICY_VERSION, timestamp: new Date().toISOString(), gpc: readGPC() };
  localStorage.setItem(KEY, JSON.stringify(doc));
  // Google Consent Mode v2 — dispatch the update immediately so it lands
  // inside the `wait_for_update: 500` window defined in index.html.
  // Uses the shared helper so the mapping logic lives in exactly one
  // place. See lib/googleConsent.js.
  applyGoogleConsent(doc);
  window.dispatchEvent(new Event("djspy-consent"));
  return doc;
}

export function ConsentProvider({ children }) {
  const [state, setState] = useState(load());

  useEffect(() => {
    // If GPC is on and no prior explicit choice → default to rejected
    if (!state && readGPC()) {
      setState(save({ analytics: false, advertising: false, reason: "gpc" }));
    }
  }, [state]);

  useEffect(() => {
    const onStorage = () => setState(load());
    window.addEventListener("storage", onStorage);
    window.addEventListener("djspy-consent", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("djspy-consent", onStorage);
    };
  }, []);

  const set = (prefs) => setState(save(prefs));
  const acceptAll = () => set({ analytics: true, advertising: true, reason: "accept-all" });
  const rejectAll = () => set({ analytics: false, advertising: false, reason: "reject-all" });
  const withdraw = () => {
    localStorage.removeItem(KEY);
    // Reset Google Consent Mode to fully-denied so any subsequent gtag
    // events run under the same restrictions as a brand-new visitor.
    applyGoogleConsent(null);
    setState(null);
    window.dispatchEvent(new Event("djspy-consent"));
  };

  return (
    <ConsentCtx.Provider value={{ prefs: state, categories: CATS, acceptAll, rejectAll, set, withdraw, policyVersion: POLICY_VERSION }}>
      {children}
    </ConsentCtx.Provider>
  );
}

export function useConsent() { return useContext(ConsentCtx); }
